import * as i0 from '@angular/core';
import { Injectable, Optional, Inject, NgModule } from '@angular/core';
import { Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import * as i1 from '@angular/router';
import { NavigationEnd } from '@angular/router';
import { isString } from '@egovsolutions/type-checking';
import * as i2 from '@angular/common';

const DEBUG_ARTIFICIAL_NAVIGATIONS = 'debug-artificial-navigations-injection-token';
class ArtificialNavigations {
    constructor(
    // Dependencies
    router, location, 
    // Passed values
    debugArtificialNavigations) {
        // Bindings
        this.router = router;
        this.location = location;
        this.debugArtificialNavigations = debugArtificialNavigations;
        // State
        this.navigatingHistory = false;
        this.currentPiles = [];
        this.currentSteps = [];
        this.mappedIds = {};
        // Dropped callbacks
        this.dropCallbacks = {};
        // Set
        this.pendingSets = [];
        // Navigation handling
        this.handlingNavigation = false;
        this.restore$ = new Subject();
        this.navigationHandled$ = new Subject();
        // Fulfill
        this.fulfilledSteps = new Set();
        this.fulfilledStepsAndDescendants = new Set();
        this.handleNavigation = this.handleNavigation.bind(this);
        this.mapId = this.mapId.bind(this);
        // Initialize
        this.init();
    }
    // Initialization
    init() {
        this.initializeNavigationHandling();
    }
    // History state updates
    setHistoryState(newState, replace) {
        if (!replace) {
            // Location doesn't have a pushState method.
            //
            // Router navigateByUrl doesn't appear to do anything when passed the current url.
            //
            // So the only way I found of pushing a new state while keeping the same URL was to
            // use window.history directly. :S
            window.history.pushState(newState, '');
        }
        else
            this.location.replaceState(this.router.url, '', newState);
        // Log
        if (this.debugArtificialNavigations)
            console.log('setHistoryState', this.getCurrentState()); // tslint:disable-line
    }
    set(optionsOrDefinitionOrType) {
        // Convert options and provide default options
        let options = this.turnDefinitionOrTypeIntoOptions(optionsOrDefinitionOrType);
        options = {
            pileIt: false,
            replace: false,
            ...options
        };
        if (!options.id)
            options.id = ++ArtificialNavigations.auxiliarId + '';
        // If currently navigating history, store and set later.
        //
        // Otherwise, continue normally.
        if (this.navigatingHistory) {
            if (this.debugArtificialNavigations)
                console.log('navigating history, storing set as pending', optionsOrDefinitionOrType); // tslint:disable-line
            this.pendingSets.push(options);
        }
        else {
            // Log
            if (this.debugArtificialNavigations)
                console.log('set', optionsOrDefinitionOrType); // tslint:disable-line
            // Clone current state
            const newHistoryState = JSON.parse(JSON.stringify(this.getCurrentState()));
            // If we're in the middle of handling a navigation and the new step definition
            // matches an existing definition, leave things as they are and keep track of
            // the new id.
            //
            // Otherwise, continue normally.
            if (this.handlingNavigation && options.definition) {
                const matchingStep = this.findMatchingDefinition(options.definition, newHistoryState.subStep);
                if (matchingStep)
                    this.mappedIds[matchingStep.id] = options.id;
            }
            else {
                // Create new step and definition
                const step = { id: options.id, definition: options.definition };
                // Set new step in appropriate place
                const parent = options.pileIt ? this.getDeepestStep(newHistoryState) : newHistoryState;
                parent.subStep = step;
                // Add to currentSteps and currentPiles
                this.currentSteps.push(options.id);
                if (!options.pileIt || !newHistoryState.subStep)
                    this.currentPiles = [[options.id]];
                else
                    this.currentPiles[this.currentPiles.length - 1].push(options.id);
                // Add or replace state
                this.setHistoryState(newHistoryState, options.replace);
            }
            // Store drop callback
            if (options.onDropped)
                this.dropCallbacks[options.id] = options.onDropped;
            // Log current piles
            if (this.debugArtificialNavigations)
                console.log('currentSteps after set', JSON.parse(JSON.stringify(this.currentSteps))); // tslint:disable-line
            if (this.debugArtificialNavigations)
                console.log('currentPiles after set', JSON.parse(JSON.stringify(this.currentPiles))); // tslint:disable-line
        }
        // Return id
        return options.id;
    }
    initializeNavigationHandling() {
        this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(this.handleNavigation);
        this.location.subscribe(this.handleNavigation);
    }
    handleNavigation() {
        // Log call
        if (this.debugArtificialNavigations)
            console.log('handleNavigation'); // tslint:disable-line
        // Clear navigatingHistory flag
        this.navigatingHistory = false;
        // Run pending sets
        while (this.pendingSets.length) {
            if (this.debugArtificialNavigations)
                console.log('pending set', this.pendingSets[0]); // tslint:disable-line
            this.set(this.pendingSets.shift());
        }
        // Set handlingNavigation flag
        this.handlingNavigation = true;
        // Initialize handledNavigation
        const handledNavigation = { restored: [], kept: [], dropped: [] };
        // Get the steps and ids for the current state
        const steps = this.getAllSteps();
        const stepIds = this.getIdsForState();
        const newPileId = stepIds[0];
        if (this.debugArtificialNavigations)
            console.log('steps stepIds, newPileId', steps, stepIds, newPileId); // tslint:disable-line
        // Drop those that aren't in the state anymore
        if (this.debugArtificialNavigations)
            console.log('this.currentSteps', JSON.parse(JSON.stringify(this.currentSteps))); // tslint:disable-line
        if (this.debugArtificialNavigations)
            console.log('this.currentPiles', JSON.parse(JSON.stringify(this.currentPiles))); // tslint:disable-line
        let idsToBeDropped = [];
        // Drop all piles if new state has no pile or new state pile nowhere in currentPiles
        let dropPiles = !newPileId || !this.currentPiles.find(pile => pile[0] === newPileId);
        const droppedPiles = [];
        for (const pile of this.currentPiles) {
            if (dropPiles) {
                droppedPiles.push(pile);
                idsToBeDropped = idsToBeDropped.concat(pile);
                continue;
            }
            if (pile[0] === newPileId) {
                const droppedIds = [];
                for (const id of pile)
                    if (stepIds.indexOf(id) === -1)
                        idsToBeDropped.push(id);
                droppedIds.forEach(id => pile.splice(pile.indexOf(id), 1));
                dropPiles = true;
            }
        }
        this.currentPiles = this.currentPiles.filter(pile => pile.length && droppedPiles.indexOf(pile) === -1);
        this.currentSteps = this.currentSteps.filter(id => idsToBeDropped.indexOf(id) === -1);
        const mappedIdsToBeDropped = idsToBeDropped.map(this.mapId);
        handledNavigation.dropped = mappedIdsToBeDropped;
        if (this.debugArtificialNavigations)
            console.log('idsToBeDropped mappedIdsToBeDropped', idsToBeDropped, mappedIdsToBeDropped); // tslint:disable-line
        while (mappedIdsToBeDropped.length) {
            const id = mappedIdsToBeDropped.shift();
            if (this.justReportedDropped !== id) {
                if (this.dropCallbacks[id])
                    this.dropCallbacks[id]();
            }
        }
        // Request the ones that aren't already opened
        const stepsToRestore = steps.filter(step => this.currentSteps.indexOf(step.id) === -1);
        for (const step of stepsToRestore) {
            // If fulfilled, skip
            if (this.fulfilledStepsAndDescendants.has(this.mapId(step.id)))
                break;
            if (this.fulfilledSteps.has(this.mapId(step.id)))
                continue;
            // Add to currentSteps and currentPiles
            this.currentSteps.push(step.id);
            if (this.currentPiles.length)
                this.currentPiles[this.currentPiles.length - 1].push(step.id);
            else
                this.currentPiles.push([step.id]);
            // Request restoration
            if (step.definition)
                this.restore$.next(step.definition);
        }
        handledNavigation.restored = stepsToRestore.map(step => this.mapId(step.id));
        // Clear handlingNavigation flag
        this.handlingNavigation = false;
        // Clear justReportedDropped
        delete this.justReportedDropped;
        // Set handledNavigation.kept and emit
        handledNavigation.kept = stepIds.filter(id => handledNavigation.restored.indexOf(id) === -1).map(this.mapId);
        this.navigationHandled$.next(handledNavigation);
        if (this.debugArtificialNavigations)
            console.log('this.currentPiles after navigation handled', JSON.parse(JSON.stringify(this.currentPiles))); // tslint:disable-line
    }
    reportDropped(id) {
        let found = false;
        let steps = 0;
        for (const step of this.getAllSteps()) {
            if (step.id === id)
                found = true;
            if (found)
                steps++;
        }
        if (steps === 1) {
            this.justReportedDropped = id;
            this.navigatingHistory = true;
            this.location.back();
        }
    }
    fulfill(id, andDescendants = false) {
        if (!andDescendants)
            this.fulfilledSteps.add(id);
        else
            this.fulfilledStepsAndDescendants.add(id);
    }
    // History and state tools
    getCurrentState() { return this.location.getState(); }
    getAllSteps(root = this.getCurrentState()) {
        const steps = [];
        let subStep = root.subStep;
        while (subStep) {
            steps.push(subStep);
            subStep = subStep.subStep;
        }
        return steps;
    }
    getIdsForState(state = this.getCurrentState()) {
        return this.getAllSteps(state).map(step => step.id);
    }
    getDeepestStep(step) {
        return step.subStep ? this.getDeepestStep(step.subStep) : step;
    }
    findMatchingDefinition(definition, step) {
        if (step)
            return (JSON.stringify(step.definition) === JSON.stringify(definition))
                ? step
                : this.findMatchingDefinition(definition, step.subStep);
        return null;
    }
    turnDefinitionOrTypeIntoOptions(optionsOrDefinitionOrType) {
        if (optionsOrDefinitionOrType) {
            if (isString(optionsOrDefinitionOrType))
                return { definition: { type: optionsOrDefinitionOrType } };
            else if ('type' in optionsOrDefinitionOrType)
                return { definition: optionsOrDefinitionOrType };
            else
                return optionsOrDefinitionOrType;
        }
        return;
    }
    mapId(oldId) {
        return this.mappedIds[oldId] ? this.mappedIds[oldId] : oldId;
    }
}
// Auxiliar ID
ArtificialNavigations.auxiliarId = 0;
ArtificialNavigations.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: ArtificialNavigations, deps: [{ token: i1.Router }, { token: i2.Location }, { token: DEBUG_ARTIFICIAL_NAVIGATIONS, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
ArtificialNavigations.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: ArtificialNavigations });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: ArtificialNavigations, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.Router }, { type: i2.Location }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [DEBUG_ARTIFICIAL_NAVIGATIONS]
                }] }]; } });

class ArtificialNavigationsModule {
}
ArtificialNavigationsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: ArtificialNavigationsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ArtificialNavigationsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: ArtificialNavigationsModule });
ArtificialNavigationsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: ArtificialNavigationsModule, providers: [ArtificialNavigations] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: ArtificialNavigationsModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [ArtificialNavigations]
                }]
        }] });

/*
 * Public API Surface of angular-artificial-navigations
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ArtificialNavigations, ArtificialNavigationsModule, DEBUG_ARTIFICIAL_NAVIGATIONS };
